/**
 * Regras da exclusão da Realização Destaque pelo cadastrante (spec `exclusao-realizacao-action-log`).
 *
 * Quem decide é a trigger `task_delete` no servidor. Estas funções decidem só o que a tela mostra:
 * se o botão aparece e se o modal pede motivo. Quando o servidor discorda (a fase virou depois que a
 * tela carregou), o modal obedece ao servidor.
 */

export type DeleteReasonOption = 'duplicada' | 'similar' | 'prioridade' | 'outros';

export const MIN_OTHER_REASON_LENGTH = 20;
export const MAX_OTHER_REASON_LENGTH = 500;

/** Textos homologados com o cliente em 2026-09-25. Os valores são os que a trigger `task_delete` aceita. */
export const DELETE_REASON_OPTIONS: ReadonlyArray<{ value: DeleteReasonOption; label: string }> = [
  { value: 'duplicada', label: 'Essa realização está duplicada' },
  { value: 'similar', label: 'Existe uma realização similar a essa' },
  { value: 'prioridade', label: 'Essa realização mudou de prioridade' },
  { value: 'outros', label: 'Outros' },
];

export const DELETE_SUCCESS_MESSAGE = 'Realização Destaque excluída com sucesso.';
export const GENERIC_DELETE_ERROR_MESSAGE =
  'Não foi possível excluir a Realização Destaque. Tente novamente em alguns minutos.';

interface WithStatus {
  status?: string;
}

export interface CanDeleteTaskInput {
  task: { owner?: string };
  userId?: string;
  /** Ciclo (semestre) de `realCycle` que contém `task.created`. Nunca a prop `cycle` do card, que é um step da tela. */
  taskCycle?: WithStatus | null;
  isView?: boolean | null;
  manager?: boolean | null;
}

export function canDeleteTask({ task, userId, taskCycle, isView, manager }: CanDeleteTaskInput): boolean {
  if (isView || manager || !userId) return false;

  return task.owner === userId && taskCycle?.status === 'open';
}

export interface DeleteRequiresReasonInput {
  task: WithStatus;
  /** Fase (mês) do ciclo da realização que contém `task.created`. */
  taskPhase?: WithStatus | null;
}

/** Só dispensa motivo a realização aguardando validação com a fase de cadastro ainda aberta. */
export function deleteRequiresReason({ task, taskPhase }: DeleteRequiresReasonInput): boolean {
  return !(task.status === 'aguardando' && taskPhase?.status === 'open');
}

export interface DeleteReasonValidation {
  valid: boolean;
  reason?: string;
  reasonOption?: DeleteReasonOption;
  /** Tamanho do texto de "Outros" sem os espaços das pontas. */
  length: number;
}

export function validateDeleteReason({ option, text }: { option?: DeleteReasonOption; text?: string }): DeleteReasonValidation {
  const typed = (text ?? '').trim();
  const length = typed.length;
  const chosen = DELETE_REASON_OPTIONS.find(item => item.value === option);

  if (!chosen) return { valid: false, length };

  if (chosen.value !== 'outros') {
    return { valid: true, reason: chosen.label, reasonOption: chosen.value, length };
  }

  if (length < MIN_OTHER_REASON_LENGTH || length > MAX_OTHER_REASON_LENGTH) return { valid: false, length };

  return { valid: true, reason: typed, reasonOption: 'outros', length };
}

/** O mínimo só aparece enquanto falta atingi-lo. */
export function formatReasonCounter(length: number): string {
  const counter = `${length} caracteres`;

  return length < MIN_OTHER_REASON_LENGTH ? `${counter} (mínimo ${MIN_OTHER_REASON_LENGTH})` : counter;
}
